import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useI18n } from "@/i18n/I18nProvider";
import { Send, ArrowUpRight } from "lucide-react";

export function ContactForm() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = t("contact.required");
    if (!form.email.trim()) e.email = t("contact.required");
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = t("contact.invalid_email");
    if (!form.message.trim()) e.message = t("contact.required");
    return e;
  };

  const onSubmit = (e) => {
    e.preventDefault();
    const eObj = validate();
    setErrors(eObj);
    if (Object.keys(eObj).length > 0) return;
    setSubmitting(true);
    // Frontend-only: redirect to /thank_you
    setTimeout(() => {
      navigate("/thank_you");
    }, 350);
  };

  const set = (k) => (ev) => setForm((f) => ({ ...f, [k]: ev.target.value }));

  return (
    <section id="contacto" className="relative py-20 lg:py-32 bg-[#0F172A] border-t border-slate-800/70" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
          <div className="lg:col-span-5">
            <div className="flex items-center gap-3">
              <span className="h-px w-8 bg-emerald-500" />
              <p className="eyebrow">{t("contact.eyebrow")}</p>
            </div>
            <h2 className="mt-5 font-display text-3xl sm:text-4xl lg:text-5xl font-bold leading-[1.05] text-balance">
              {t("contact.heading")}
            </h2>
            <p className="mt-5 text-base text-slate-400 leading-relaxed">{t("contact.subtitle")}</p>

            <a href="mailto:hola@zantravika.com" className="mt-8 inline-flex items-center gap-2 text-sm text-emerald-300 font-mono uppercase tracking-widest hover:text-white transition-colors" data-testid="contact-email-link">
              hola@zantravika.com <ArrowUpRight className="w-4 h-4" />
            </a>
          </div>

          <motion.form
            className="lg:col-span-7 z-card p-6 lg:p-10"
            onSubmit={onSubmit}
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.15 }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            data-testid="contact-form"
            noValidate
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <FormField
                label={t("contact.name")}
                placeholder={t("contact.name_placeholder")}
                value={form.name}
                onChange={set("name")}
                error={errors.name}
                testId="contact-form-name-input"
                errorTestId="contact-form-name-error"
                autoComplete="name"
              />
              <FormField
                label={t("contact.email")}
                placeholder={t("contact.email_placeholder")}
                value={form.email}
                onChange={set("email")}
                error={errors.email}
                type="email"
                testId="contact-form-email-input"
                errorTestId="contact-form-email-error"
                autoComplete="email"
              />
            </div>

            <div className="mt-5">
              <label className="block font-mono text-[11px] uppercase tracking-widest text-slate-400 mb-2">
                {t("contact.message")}
              </label>
              <textarea
                value={form.message}
                onChange={set("message")}
                placeholder={t("contact.message_placeholder")}
                rows={5}
                className="w-full rounded-xl bg-[#0B132B] border border-slate-800 focus:border-emerald-500/70 focus:ring-2 focus:ring-emerald-500/20 outline-none px-4 py-3 text-sm text-white placeholder:text-slate-500 transition"
                data-testid="contact-form-message-input"
              />
              {errors.message && <p className="mt-2 text-xs text-red-400" data-testid="contact-form-message-error">{errors.message}</p>}
            </div>

            <div className="mt-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <p className="text-[11px] text-slate-500 max-w-md leading-relaxed">
                {t("footer.risk")}
              </p>
              <button type="submit" disabled={submitting} className="btn-emerald text-sm shrink-0 disabled:opacity-60" data-testid="contact-form-submit-button">
                {t("contact.submit")} <Send className="w-4 h-4" />
              </button>
            </div>
          </motion.form>
        </div>
      </div>
    </section>
  );
}

function FormField({ label, placeholder, value, onChange, error, type = "text", testId, errorTestId, autoComplete }) {
  return (
    <div>
      <label className="block font-mono text-[11px] uppercase tracking-widest text-slate-400 mb-2">{label}</label>
      <input
        type={type}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        autoComplete={autoComplete}
        className="w-full rounded-xl bg-[#0B132B] border border-slate-800 focus:border-emerald-500/70 focus:ring-2 focus:ring-emerald-500/20 outline-none px-4 py-3 text-sm text-white placeholder:text-slate-500 transition"
        data-testid={testId}
      />
      {error && <p className="mt-2 text-xs text-red-400" data-testid={errorTestId}>{error}</p>}
    </div>
  );
}

export default ContactForm;
